import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import groupService from '../api/groupService';
import config from '../config';

const NameClaimSignUp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const claimCode = searchParams.get('claim');
  const recipientName = searchParams.get('name');
  const claimType = searchParams.get('type'); // 'name' or 'pledge'
  
  const { registerAdmin, currentUser, isAuthenticated } = useAuth();
  const { addNewAdminGroup, adminGroups, loading: groupLoading } = useGroup();

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [creationStep, setCreationStep] = useState('');

  const [communityName, setCommunityName] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  
  // Validation states
  const [communityNameError, setCommunityNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [checkingCommunityName, setCheckingCommunityName] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  useEffect(() => {
    if (!claimCode) {
      navigate('/');
    }
  }, [claimCode, navigate]);

  // Check if user is already authenticated - redirect to dashboard if not in creation process
  useEffect(() => {
    if (currentUser && isAuthenticated() && !creationStep && !sessionStorage.getItem('communityCreationInProgress')) {
      console.log('🚀 User already authenticated, redirecting to dashboard');
      if (currentUser.role === 'admin') {
        if (!groupLoading && adminGroups !== null) {
          navigate('/admin/dashboard/members', { replace: true });
        }
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [currentUser, isAuthenticated, navigate, creationStep, adminGroups, groupLoading]);

  const handleCommunityNameChange = (e) => {
    const value = e.target.value;
    setCommunityName(value);
    setError('');
    setCommunityNameError('');
    
    // Debounced validation
    if (value.trim().length >= 3) {
      clearTimeout(window.communityNameTimeout);
      window.communityNameTimeout = setTimeout(() => {
        checkCommunityNameAvailability(value.trim());
      }, 500);
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setError('');
    setEmailError('');
    
    // Debounced validation
    if (value.trim() && value.includes('@')) {
      clearTimeout(window.emailTimeout);
      window.emailTimeout = setTimeout(() => {
        checkEmailAvailability(value.trim());
      }, 500);
    }
  };

  const checkCommunityNameAvailability = async (name) => {
    try {
      setCheckingCommunityName(true);
      const response = await groupService.checkGroupNameAvailability(name);
      if (!response.available) {
        setCommunityNameError('This community name is already taken. Please choose another.');
      }
    } catch (error) {
      console.error('Error checking community name:', error);
    } finally {
      setCheckingCommunityName(false);
    }
  };

  const checkEmailAvailability = async (emailAddress) => {
    try {
      setCheckingEmail(true);
      const response = await fetch('/api/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailAddress })
      });
      const data = await response.json();
      if (!data.available) {
        setEmailError('This email is already registered. Please use a different email or log in.');
      }
    } catch (error) {
      console.error('Error checking email:', error);
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file size must be less than 5MB');
        return;
      }
      
      setUploadedLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (currentStep === 1) {
      if (!communityName.trim()) {
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(3);
    } else if (currentStep === 3) {
      setIsLoading(true);
      setError('');
      
      const isCreating = sessionStorage.getItem('communityCreationInProgress');
      if (isCreating) {
        console.log('🎯 Creation already in progress, ignoring duplicate submission');
        return;
      }
      
      sessionStorage.setItem('communityCreationInProgress', 'true');
      
      try {
        // Step 1: Create Admin Account
        setCreationStep('Creating your account...');
        console.log('🔄 Registering admin user...');
        const adminData = {
          name: fullName.trim(),
          email: email.trim(),
          password: password,
          role: 'admin'
        };
        
        const registrationResult = await registerAdmin(adminData);
        console.log('✅ Admin user registered successfully:', registrationResult.user.email);
        
        // Step 2: Create Community
        setCreationStep('Setting up your community...');
        console.log('🔄 Creating group...');
        
        const groupData = {
          name: communityName.trim(),
          adminName: communityName.trim()
        };
        
        const groupResult = await groupService.createGroup(groupData);
        console.log('✅ Group created successfully:', groupResult.data.id);
        
        // Step 2.5: Upload logo if provided
        if (uploadedLogo) {
          setCreationStep('Uploading logo...');
          console.log('🖼️ Uploading logo to created group...');
          
          try {
            const logoBase64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(uploadedLogo);
            });
            
            const updateData = {
              name: communityName.trim(),
              description: groupResult.data.description || '',
              logoFile: logoBase64
            };
            
            const logoResult = await groupService.updateGroup(groupResult.data.id, updateData);
            console.log('✅ Logo uploaded successfully');
            
            // Update the groupResult with the new logo URL
            groupResult.data.logo_url = logoResult.data.logo_url;
          } catch (logoError) {
            console.warn('⚠️ Logo upload failed, but group was created successfully:', logoError);
          }
        }
        
        // Step 3: Add the new group to context immediately
        setCreationStep('Finalizing...');
        console.log('🎯 Adding newly created group to context immediately...');
        
        const success = addNewAdminGroup(groupResult.data, registrationResult.user.id);
        if (success) {
          console.log('✅ New admin group added to context immediately');
        } else {
          console.warn('⚠️ Failed to add group to context');
        }
        
        // Step 4: Accept the claim (name or pledge) to the newly created group
        const isNameClaim = !claimType || claimType === 'name';
        const isPledgeClaim = claimType === 'pledge';
        
        setCreationStep(isPledgeClaim ? 'Processing pledge...' : 'Adding name to your prayer list...');
        console.log(isPledgeClaim ? '💰 Accepting pledge to group...' : '🙏 Accepting name request to group...');
        
        const API_URL = config.API_URL;
        const apiEndpoint = isPledgeClaim 
          ? `${API_URL}/pledges/${claimCode}/accept-to-group`
          : `${API_URL}/names/${claimCode}/accept-to-group`;
        
        // For pledges, send group_id. For names, send prayer_group_id
        const requestBody = isPledgeClaim 
          ? { group_id: groupResult.data.id }
          : { prayer_group_id: groupResult.data.id };
        
        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(requestBody)
        });

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || `Failed to accept ${isPledgeClaim ? 'pledge' : 'name request'}`);
        }
        
        console.log(isPledgeClaim ? '✅ Pledge accepted successfully' : '✅ Name request accepted successfully');
        
        sessionStorage.removeItem('communityCreationInProgress');
        
        // Step 5: Add a small delay to ensure context update completes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Success! Navigate to the appropriate dashboard page
        console.log('🚀 Setup complete, redirecting to dashboard');
        const dashboardRoute = isPledgeClaim ? '/admin/dashboard/financials/pledges' : '/admin/dashboard/prayer-list';
        navigate(dashboardRoute, { 
          state: { 
            claimCode: claimCode,
            showClaimSuccess: true
          },
          replace: true
        });

      } catch (error) {
        console.error('❌ Signup error:', error);
        setError(error.message || 'An error occurred during signup. Please try again.');
        setCreationStep('');
        sessionStorage.removeItem('communityCreationInProgress');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f9fafb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '640px', width: '100%' }}>
        {/* Back Button - Shows only on steps 2 and 3 */}
        {currentStep > 1 && (
          <button
            type="button"
            onClick={() => setCurrentStep(currentStep === 3 ? 2 : 1)}
            disabled={currentStep === 3 && isLoading}
            style={{
              background: 'none',
              border: 'none',
              color: '#6b7280',
              fontSize: '14px',
              cursor: currentStep === 3 && isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginBottom: '20px',
              padding: '0',
              opacity: currentStep === 3 && isLoading ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!(currentStep === 3 && isLoading)) {
                e.target.style.color = '#374151';
              }
            }}
            onMouseLeave={(e) => {
              if (!(currentStep === 3 && isLoading)) {
                e.target.style.color = '#6b7280';
              }
            }}
          >
            ← Back
          </button>
        )}

        {/* Yesod Logo - Above all steps */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
          <img 
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png"
            alt="Yesod"
            style={{ width: '80px', height: 'auto' }}
          />
        </div>

        {/* Progress Bar - Below back button, centered */}
        <div style={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'center',
          marginBottom: '32px'
        }}>
          <div style={{
            width: '60px',
            height: '4px',
            background: currentStep >= 1 ? '#8b5cf6' : '#e5e7eb',
            borderRadius: '2px'
          }}></div>
          <div style={{
            width: '60px',
            height: '4px',
            background: currentStep >= 2 ? '#8b5cf6' : '#e5e7eb',
            borderRadius: '2px'
          }}></div>
          <div style={{
            width: '60px',
            height: '4px',
            background: currentStep >= 3 ? '#8b5cf6' : '#e5e7eb',
            borderRadius: '2px'
          }}></div>
        </div>

        <AnimatePresence mode="wait">
          {currentStep === 1 ? (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h1 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#000',
                marginBottom: '12px',
                textAlign: 'center'
              }}>
                Create Your Community
              </h1>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <p style={{
                    fontSize: '16px',
                    color: '#6b7280',
                    marginBottom: '20px'
                  }}>
                    Type your community name below to get started
                  </p>
                  
                  <input
                    type="text"
                    value={communityName}
                    onChange={handleCommunityNameChange}
                    placeholder="e.g., Beth Shalom, Chabad of Westville, MJE"
                    required
                    maxLength={100}
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '16px',
                      fontSize: '16px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      background: '#fff'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                  
                  {/* Community name validation feedback */}
                  {checkingCommunityName && (
                    <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '8px' }}>
                      Checking availability...
                    </p>
                  )}
                  {communityNameError && (
                    <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px' }}>
                      {communityNameError}
                    </p>
                  )}
                </div>

                <button 
                  type="submit" 
                  disabled={isLoading || !communityName.trim() || checkingCommunityName || !!communityNameError}
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#fff',
                    background: '#8b5cf6',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isLoading || !communityName.trim() || checkingCommunityName || !!communityNameError ? 'not-allowed' : 'pointer',
                    opacity: isLoading || !communityName.trim() || checkingCommunityName || !!communityNameError ? 0.5 : 1,
                    marginBottom: '12px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading && communityName.trim()) {
                      e.target.style.background = '#7c3aed';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#8b5cf6';
                  }}
                >
                  Next: Add Logo
                </button>
              </form>
            </motion.div>
          ) : currentStep === 2 ? (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h1 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#000',
                marginBottom: '12px',
                textAlign: 'center'
              }}>
                Add Community Logo
              </h1>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <p style={{
                    fontSize: '16px',
                    color: '#6b7280',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    Helps members recognize your page immediately.
                  </p>

                  {logoPreviewUrl ? (
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                      <img 
                        src={logoPreviewUrl} 
                        alt="Logo preview" 
                        style={{ 
                          maxWidth: '200px', 
                          maxHeight: '200px',
                          borderRadius: '12px',
                          marginBottom: '20px',
                          display: 'block',
                          margin: '0 auto 20px'
                        }} 
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setUploadedLogo(null);
                          setLogoPreviewUrl(null);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#3b82f6',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontSize: '14px'
                        }}
                      >
                        Remove logo
                      </button>
                    </div>
                  ) : (
                    <label 
                      htmlFor="logo-upload"
                      style={{
                        display: 'block',
                        padding: '60px 40px',
                        border: '2px dashed #cbd5e0',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        background: '#fff',
                        textAlign: 'center',
                        marginBottom: '20px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.background = '#f0f9ff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#cbd5e0';
                        e.target.style.background = '#fff';
                      }}
                    >
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>📸</div>
                      <div style={{ color: '#6b7280', fontSize: '16px' }}>Click to upload</div>
                      <div style={{ fontSize: '14px', color: '#9ca3af', marginTop: '5px' }}>
                        JPG or PNG, max 5MB
                      </div>
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>

                <button 
                  type="submit"
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#fff',
                    background: '#8b5cf6',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    marginBottom: '12px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#7c3aed'}
                  onMouseLeave={(e) => e.target.style.background = '#8b5cf6'}
                >
                  Next: Account Details
                </button>

                <button 
                  type="button"
                  onClick={() => setCurrentStep(3)}
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#9ca3af',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.2s',
                    marginBottom: '32px'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#6b7280'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                >
                  Upload later
                </button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h1 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#000',
                marginBottom: '12px',
                textAlign: 'center'
              }}>
                Secure Admin Access
              </h1>

              <form onSubmit={handleSubmit}>
                {error && (
                  <div style={{
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    color: '#991b1b',
                    fontSize: '14px'
                  }}>
                    {error}
                  </div>
                )}

                {creationStep && (
                  <div style={{
                    background: '#dbeafe',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    color: '#1e40af',
                    textAlign: 'center',
                    fontSize: '14px'
                  }}>
                    {creationStep}
                  </div>
                )}

                <p style={{
                  fontSize: '16px',
                  color: '#6b7280',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  Create a login to manage your community.
                </p>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="fullName" style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Your Full Name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Your Name"
                    required
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '16px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      background: '#fff'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="email" style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="admin@yourshul.org"
                    required
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '16px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      background: '#fff'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                  
                  {/* Email validation feedback */}
                  {checkingEmail && (
                    <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '8px' }}>
                      Checking availability...
                    </p>
                  )}
                  {emailError && (
                    <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px' }}>
                      {emailError}
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="password" style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      required
                      minLength={6}
                      disabled={isLoading}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        paddingRight: '50px',
                        fontSize: '16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        background: '#fff'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#6b7280',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>

                <button 
                  type="submit" 
                  disabled={isLoading || !fullName || !email || !password || checkingEmail || !!emailError}
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#fff',
                    background: '#8b5cf6',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isLoading || !fullName || !email || !password || checkingEmail || !!emailError ? 'not-allowed' : 'pointer',
                    opacity: isLoading || !fullName || !email || !password || checkingEmail || !!emailError ? 0.5 : 1,
                    marginBottom: '16px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading && fullName && email && password && !checkingEmail && !emailError) {
                      e.target.style.background = '#7c3aed';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#8b5cf6';
                  }}
                >
                  {isLoading ? 'Creating...' : 'Complete & Accept Name'}
                </button>

                <p style={{ 
                  fontSize: '13px', 
                  color: '#6b7280', 
                  textAlign: 'center',
                  marginBottom: '32px'
                }}>
                  By creating an account, you agree to our{' '}
                  <button 
                    type="button"
                    onClick={() => window.open('/terms-of-service#/terms-of-service', '_blank')}
                    style={{ 
                      textDecoration: 'underline',
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: 'inherit'
                    }}
                  >
                    terms of service
                  </button>{' '}
                  and{' '}
                  <button 
                    type="button"
                    onClick={() => window.open('/terms-of-service#/privacy-policy', '_blank')}
                    style={{ 
                      textDecoration: 'underline',
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: 'inherit'
                    }}
                  >
                    privacy policy
                  </button>
                  .
                </p>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default NameClaimSignUp;
